import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckSquare, Receipt, Users, FileText, Keyboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// true se il focus è su un campo di testo (per non rubare le scorciatoie mentre si scrive)
function isTyping() {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

// Navigazione rapida: "g" poi una lettera
const GOTO = {
  h: '/', c: '/clienti-attivi', t: '/task', s: '/scadenzario',
  p: '/ped', a: '/analytics', l: '/leads', e: '/calendario',
};

export default function QuickActions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin';
  const [open, setOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const gPending = useRef(false);
  const gTimer = useRef(null);

  const actions = [
    { label: 'Nuova task',    icon: CheckSquare, to: '/task?new=1' },
    { label: 'Nuovo contenuto PED', icon: FileText, to: '/ped/contenuto/nuovo' },
    ...(isAdmin ? [{ label: 'Nuova fattura', icon: Receipt, to: '/fatturazione?new=1' }] : []),
    { label: 'Nuovo cliente (onboarding)', icon: Users, to: '/onboarding' },
  ];

  const go = useCallback((to) => { setOpen(false); navigate(to); }, [navigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (isTyping() || e.metaKey || e.ctrlKey || e.altKey) return;
      // sequenza "g" + lettera
      if (gPending.current) {
        const dest = GOTO[e.key.toLowerCase()];
        gPending.current = false;
        if (gTimer.current) clearTimeout(gTimer.current);
        if (dest) { e.preventDefault(); navigate(dest); return; }
      }
      if (e.key === 'g') {
        gPending.current = true;
        gTimer.current = setTimeout(() => { gPending.current = false; }, 900);
      } else if (e.key === 'n') {
        e.preventDefault(); setOpen(o => !o);
      } else if (e.key === '?') {
        e.preventDefault(); setShowHelp(h => !h);
      } else if (e.key === 'Escape') {
        setOpen(false); setShowHelp(false);
      }
    };
    const onOpenQuick = () => setOpen(o => !o);
    window.addEventListener('keydown', onKey);
    window.addEventListener('eden-open-quickadd', onOpenQuick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('eden-open-quickadd', onOpenQuick);
    };
  }, [navigate]);

  return (
    <>
      {/* Quick-add menu */}
      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 700 }}>
          <div onClick={() => setOpen(false)} className="eden-quick-backdrop" style={{ position: 'absolute', inset: 0 }} />
          <div className="eden-toast-in eden-quick-menu" style={{
            position: 'absolute', top: 60, right: 72, width: 240,
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
            boxShadow: 'var(--shadow-lg)', padding: 6,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 10px 4px' }}>Crea</div>
            {actions.map(a => {
              const Icon = a.icon;
              return (
                <div key={a.label} onClick={() => go(a.to)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 8, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-h)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <Icon size={15} color="var(--cyan)" />
                  <span style={{ fontSize: 13.5, color: 'var(--text)' }}>{a.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Help scorciatoie (?) */}
      {showHelp && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={() => setShowHelp(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }} />
          <div className="eden-toast-in" style={{ position: 'relative', width: 'min(420px, 100%)', background: 'var(--bg2, #14141c)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Keyboard size={16} color="var(--cyan)" />
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Scorciatoie da tastiera</span>
            </div>
            {[
              ['⌘K / Ctrl+K', 'Ricerca globale'],
              ['n', 'Menu crea'],
              ['g poi h', 'Home'],
              ['g poi c', 'Clienti attivi'],
              ['g poi t', 'Task'],
              ['g poi s', 'Scadenzario'],
              ['g poi p', 'Piano editoriale'],
              ['g poi a', 'Analytics'],
              ['?', 'Questo aiuto'],
            ].map(([k, d]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>{d}</span>
                <kbd style={{ fontSize: 11, color: 'var(--text)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, padding: '2px 8px' }}>{k}</kbd>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
